import type { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import React from 'react'
import Styles from '../styles'
import './_app.scss'

const Layout = dynamic(() => import('core/layout'))

// Module-level guard: prevents the stale-build reload from firing more than
// once per page load. Without this, a "Failed to load static props" error on a
// soft navigation triggers replace() → another soft nav → another error →
// infinite loop → RangeError: Maximum call stack size exceeded.
let _reloadScheduled = false

const App = ({ Component, pageProps }: AppProps) => {
  const router = useRouter()
  const { locale } = router

  // Handle stale build-ID errors after a new deployment.
  // When the server has a newer build, all _next/data fetches from the old
  // client bundle return 404 → Next.js throws "Failed to load static props".
  // Forcing a full hard reload fetches the new HTML + JS bundle.
  React.useEffect(() => {
    const handleError = (err: Error, url: string) => {
      if (err.message !== 'Failed to load static props') return
      // Only reload once — re-entrancy guard prevents the infinite loop that
      // occurs when the reload itself triggers another routeChangeError.
      if (_reloadScheduled) return
      _reloadScheduled = true

      // Always do a hard reload rather than replace().
      //
      // Why not replace(url)?
      //   - url from routeChangeError is a pathname (/fr/products/...), not an
      //     absolute URL. When the user is already on that page, Next.js 12
      //     intercepts location.replace() synchronously and throws:
      //     "Invariant: attempted to hard navigate to the same URL"
      //   - Even for navigations to a different page, the failed navigation
      //     means the user is still on the current page, so reload() is always
      //     correct — it fetches the current URL with the new build assets.
      //
      // Why setTimeout(0)?
      //   - Defers out of Next.js's synchronous routeChangeError dispatch so
      //     the reload fires after the current event stack unwinds.
      //
      // Reset the guard after 5 seconds. If window.location.reload() is
      // blocked (e.g. by a browser extension or service worker), the user
      // would otherwise be permanently stuck with no recovery path.
      setTimeout(() => window.location.reload(), 0)
      setTimeout(() => { _reloadScheduled = false }, 5000)
    }
    router.events.on('routeChangeError', handleError)
    return () => {
      router.events.off('routeChangeError', handleError)
    }
  }, [router])

  React.useEffect(() => {
    document.documentElement.lang = locale?.toString() || 'en-US'
  }, [locale])

  if (pageProps.layout) {
    return (
      <Layout {...pageProps} {...{
        Styles,
        locale,
        gtm: process.env.NEXT_PUBLIC_GTM_ID
      }}>
        <Head>
          <script
            async
            type="text/javascript"
            src="https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=URESF6"
          />
        </Head>
        <Component {...pageProps} />
      </Layout>
    )
  }
  return <Component {...pageProps} />
}

export default App
